import { parseExpression, parseSegment, tokenize } from "./parser.js";
import { resolveUnit } from "./units.js";
import {
  evalExpr,
  Facts,
  solveDim,
  hasCustomDenominator,
  prepareQuantities,
  resolveQuantities,
} from "./engine.js";
import { CalcError } from "./model.js";
import type { Quantity } from "./model.js";

/** Pre-scan a scope for custom units written plural (`users`) → render them plural. */
function collectPlurals(text: string, facts: Facts): void {
  for (const line of text.split("\n")) {
    const code = line.split("--")[0] ?? "";
    let toks;
    try {
      toks = tokenize(code);
    } catch {
      continue;
    }
    for (const t of toks) {
      if (t.type === "word" && t.value.length >= 3 && t.value.endsWith("s")) {
        const ru = resolveUnit(t.value);
        if (ru.family === "count") facts.markPlural(ru.dim);
      }
    }
  }
}

/** Strip the common leading indentation from a block's lines. */
function dedent(text: string): string {
  const lines = text.split("\n");
  let min = Infinity;
  for (const l of lines) {
    if (l.trim() === "") continue;
    min = Math.min(min, (l.match(/^[ \t]*/)?.[0] ?? "").length);
  }
  if (!isFinite(min) || min === 0) return text;
  return lines.map((l) => l.slice(min)).join("\n");
}

export type LineKind = "blank" | "fact" | "query" | "error";

export interface LineResult {
  input: string;
  kind: LineKind;
  /** rendered result for a query, or echo for a fact */
  output?: string;
  error?: string;
}

function stripComment(line: string): string {
  const i = line.indexOf("--");
  return i === -1 ? line : line.slice(0, i);
}

/**
 * Run a multi-line worksheet. Facts (lines without `=`) accumulate; queries
 * (lines with `=`) are evaluated against the facts seen so far.
 */
export function run(text: string, precise = false): LineResult[] {
  const facts = new Facts(precise);
  collectPlurals(text, facts);
  const results: LineResult[] = [];

  for (const raw of text.split("\n")) {
    const code = stripComment(raw).trim();
    if (code === "") {
      results.push({ input: raw, kind: "blank" });
      continue;
    }

    try {
      if (code.includes("?")) {
        const parts = code.split("?");
        const exprStr = (parts[0] ?? "").trim();
        const segs = parts.slice(1);
        results.push({ input: raw, kind: "query", output: runQuery(exprStr, segs, facts).join(" ; ") });
      } else if (isComputation(code)) {
        // a math expression (has an operation) is evaluated without `?`
        results.push({ input: raw, kind: "query", output: runQuery(code, [""], facts).join(" ; ") });
      } else {
        // a bare quantity is a declaration: register if it's a fact/flow/population,
        // otherwise it's a harmless no-op value (parse failures still throw → error)
        for (const stmtStr of code.split(",")) {
          if (stmtStr.trim() === "") continue;
          classifyStatement(evalExpr(parseExpression(stmtStr)), facts);
        }
        results.push({ input: raw, kind: "fact" });
      }
    } catch (e) {
      const msg = e instanceof CalcError ? e.message : e instanceof Error ? e.message : String(e);
      results.push({ input: raw, kind: "error", error: msg });
    }
  }

  return results;
}

/** Convenience: run and return only the rendered query outputs, in order. */
export function evalQueries(text: string, precise = false): string[] {
  return run(text, precise)
    .filter((r) => r.kind === "query")
    .map((r) => r.output ?? "");
}

/** Register a statement quantity as a rate fact, flow, or population. Returns false
 *  if it's none of those (a bare value or prose). */
function classifyStatement(q: import("./model.js").Quantity, facts: Facts): boolean {
  if (hasCustomDenominator(q)) {
    facts.add(q);
    return true;
  }
  if (Facts.isPopulation(q)) {
    facts.addPopulation(q);
    return true;
  }
  if (Facts.isFlow(q)) {
    facts.addFlow(q);
    return true;
  }
  return false;
}

// A bare unit/dimension token (`req`, `gb`, `melon`, `$`) — letters or `$`, no
// digits, so a value like `$5` or `10b` is never mistaken for one.
const BARE_DIM_RE = /^[A-Za-z$][A-Za-z]*$/;

/**
 * A query line whose LHS is itself a statement (a fact / flow / population)
 * contributes it to scope, so `100m urls/mo = ?/mo` both displays the rate AND
 * makes the flow available to later queries. Bare-dimension LHS (`req`) is never
 * registered — it would otherwise be misread as a population of 1.
 */
function registerQueryLhs(exprStr: string, facts: Facts): void {
  if (BARE_DIM_RE.test(exprStr)) return;
  try {
    classifyStatement(evalExpr(parseExpression(exprStr)), facts);
  } catch {
    /* not a statement-shaped LHS */
  }
}

/** Does this string parse as a calc expression? (Used to tell a query from prose.) */
function parsesAsExpr(s: string): boolean {
  if (s.trim() === "") return false;
  try {
    parseExpression(s);
    return true;
  } catch {
    return false;
  }
}

/** A computation — parses as a binary operation (`a * b`, `a + b`, `a / b`). These
 *  are evaluated without a `?`; a bare quantity is a declaration. */
function isComputation(s: string): boolean {
  try {
    return parseExpression(s).t === "bin";
  } catch {
    return false;
  }
}

/** Populations + flows in scope — the starting amounts for a graph solve. */
function anchorStarts(facts: Facts): Quantity[] {
  return [...facts.populations(), ...facts.flows()];
}

/** Zero of a dimension — a bare-dim query with nothing in scope is `0 <unit>`. */
function zeroOf(dim: string): Quantity {
  return { value: 0, dims: new Map([[dim, 1]]), solved: true };
}

/** `? PB`, `? Gbit/s` — no left side: solve for the spec's unit over the graph. */
function solveForSpec(seg: string, facts: Facts): string {
  const spec = parseSegment(seg);
  const numUnit =
    spec.kind === "unit" ? spec.factors.find((f) => f.op === "*" && f.unit !== "?")?.unit : undefined;
  if (!numUnit) throw new CalcError("name a unit to solve for, e.g. `? PB`");
  const dim = resolveUnit(numUnit).dim;
  const got = solveDim(anchorStarts(facts), dim, facts, true);
  return resolveQuantities(got.length ? got : [zeroOf(dim)], spec, facts);
}

/** Evaluate `lhs ? seg ? seg…`: one rendered result per segment (bare-dim → one). */
function runQuery(exprStr: string, segs: string[], facts: Facts): string[] {
  const cyc = facts.findCycle();
  if (cyc) throw new CalcError(`cyclic facts: '${cyc}' is defined in terms of itself`);
  // `? PB` (no left side) → solve for the unit named in each segment
  if (exprStr === "") return segs.map((s) => solveForSpec(s, facts));
  // bare dimension (`reads ?`, `reads ?/s`): the total of that dim over the graph,
  // resolved per segment so the spec still applies. Don't register it — a lone dim
  // is not a statement (it would become a bogus population of 1).
  const isBare = BARE_DIM_RE.test(exprStr);
  if (isBare) {
    const dim = resolveUnit(exprStr).dim;
    const got = solveDim(anchorStarts(facts), dim, facts, true);
    const tot = got.length ? got : [zeroOf(dim)]; // nothing in scope → 0 of it
    return segs.map((s) => resolveQuantities(tot, parseSegment(s), facts));
  }
  registerQueryLhs(exprStr, facts);
  const qs = prepareQuantities(exprStr, facts);
  return segs.map((s) => resolveQuantities(qs, parseSegment(s), facts));
}

/** Register comma-separated statements in a `~{ }` block; ignore non-statements (prose). */
function registerStatement(code: string, facts: Facts): void {
  for (const stmt of code.split(",")) {
    if (stmt.trim() === "") continue;
    try {
      classifyStatement(evalExpr(parseExpression(stmt)), facts);
    } catch {
      /* prose / partial line */
    }
  }
}

/** A calc problem inside a `~{ … }` region, offset relative to the region body. */
export interface CalcIssue {
  readonly message: string;
  readonly offset: number;
  readonly length: number;
}

function errMessage(e: unknown): string {
  return e instanceof CalcError ? e.message : e instanceof Error ? e.message : "invalid expression";
}

/**
 * Render the body of a `~{ … }` estimate region: plain prose with calc results
 * filled in. **Only `=` produces output** — statements (facts / flows /
 * populations) register and pass through verbatim, and everything else is prose,
 * left untouched. A line `lhs = a = b` is rewritten `lhs = <r(a)> = <r(b)>`.
 *
 * Returns the rendered text plus calc issues (only broken `=` queries are flagged;
 * non-`=` lines that don't parse are prose, never flagged — annotations are free).
 */
export function renderBlockWithIssues(text: string, precise = false): { text: string; issues: CalcIssue[] } {
  const facts = new Facts(precise);
  collectPlurals(text, facts);
  const issues: CalcIssue[] = [];

  let offset = 0;
  const out = text.split("\n").map((line) => {
    const lineOffset = offset;
    offset += line.length + 1; // + newline
    const ci = line.indexOf("--");
    const codePart = ci === -1 ? line : line.slice(0, ci);
    const comment = ci === -1 ? "" : line.slice(ci);
    const code = codePart.trim();
    if (code === "") return line; // blank / comment-only

    // A query is a line with `?` whose left-hand side parses as an expression (or is
    // empty — a `? PB`-style solve); otherwise the `?` is prose and passes through.
    const parts = codePart.split("?");
    const exprStr = (parts[0] ?? "").trim();
    if (parts.length > 1 && (parsesAsExpr(exprStr) || exprStr === "")) {
      const lead = codePart.match(/^\s*/)?.[0] ?? "";
      const segs = parts.slice(1);
      const emptyLhs = exprStr === "";
      const isBare = !emptyLhs && BARE_DIM_RE.test(exprStr);
      // don't register a bare dim (it would become a bogus population of 1)
      if (!emptyLhs && !isBare) registerQueryLhs(exprStr, facts);

      let qs: Quantity[] | undefined;
      try {
        const cyc = facts.findCycle();
        if (cyc) throw new CalcError(`cyclic facts: '${cyc}' is defined in terms of itself`);
        if (isBare) {
          const dim = resolveUnit(exprStr).dim;
          const got = solveDim(anchorStarts(facts), dim, facts, true);
          qs = got.length ? got : [zeroOf(dim)];
        } else if (!emptyLhs) {
          qs = prepareQuantities(exprStr, facts);
        }
      } catch (e) {
        issues.push({ message: errMessage(e), offset: lineOffset, length: line.length });
        return line; // LHS itself is broken → leave as written
      }

      // Resolve each segment independently: a broken segment is flagged and left
      // as written, but the others still fill in.
      const rendered = segs.map((s) => {
        try {
          return emptyLhs ? solveForSpec(s, facts) : resolveQuantities(qs!, parseSegment(s), facts);
        } catch (e) {
          issues.push({ message: errMessage(e), offset: lineOffset, length: line.length });
          return s.trim() === "" ? "?" : s.trim();
        }
      });
      const shown = emptyLhs ? code : exprStr; // keep "? PB" before the arrow
      return `${lead}${shown} → ${rendered.join(" → ")}${comment ? `  ${comment}` : ""}`;
    }

    // A computation (has an operation) is evaluated without `?`.
    if (isComputation(code)) {
      const lead = codePart.match(/^\s*/)?.[0] ?? "";
      try {
        const result = runQuery(code, [""], facts).join(" ; ");
        return `${lead}${code} → ${result}${comment ? `  ${comment}` : ""}`;
      } catch (e) {
        issues.push({ message: errMessage(e), offset: lineOffset, length: line.length });
        return line;
      }
    }

    registerStatement(code, facts); // bare quantity (declaration) or prose (ignored)
    return line;
  });

  return { text: dedent(out.join("\n")), issues };
}

export function renderBlock(text: string, precise = false): string {
  return renderBlockWithIssues(text, precise).text;
}
